import {ReactNode, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {ErrorPage} from "../ErrorPage/ErrorPage";
import {LoadingPage} from "../LoadingPage/LoadingPage";
import {ThemedPage} from "../ThemedPage/ThemedPage";
import {ThemeClasses} from "../../../theme/useContentStyles";
import {Entity, extractErrorMessage} from "@ots-frontend/ots-data";

export type ProtectedPageProps = {
  entity?: Entity
  permissions?: string | string[]
  error?: any
  isLoading?: boolean
  onAccess?: (boolean) => void
  children: ((classes: ThemeClasses) => ReactNode | undefined) | ReactNode | undefined
}

export function ProtectedPage(props: ProtectedPageProps) {
  const {entity, permissions = [], isLoading = true, error, onAccess, children} = props;

  const [access, setAccess] = useState<boolean | undefined>(undefined);
  const [showLoader, setShowLoader] = useState(false);

  const {hasAccess, hasEntityAccess} = useAuth()
  const {t} = useTranslation(["common"])

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (typeof access === 'undefined' || isLoading) {
      timer = setTimeout(() => setShowLoader(true), 500);
    } else {
      clearTimeout(timer);
      setShowLoader(false);
    }
    return () => clearTimeout(timer);
  }, [access, isLoading]);

  const requiredPermissions = useMemo(() => {
    if (permissions) {
      if (typeof permissions === 'string') {
        return [permissions]
      } else {
        return permissions
      }
    }

    return []
  }, [permissions])

  useEffect(() => {
    let access: boolean
    if (entity) {
      access = hasEntityAccess(entity, ...requiredPermissions)
    } else {
      access = hasAccess(...requiredPermissions)
    }
    setAccess(access);
    if (onAccess) {
      onAccess(access);
    }
  }, [entity, hasAccess, hasEntityAccess, onAccess, requiredPermissions]);

  // pending load
  if (isLoading && !showLoader) {
    return undefined;
  }

  // loading
  if (isLoading && showLoader) {
    return (
      <LoadingPage/>
    );
  }

  // pending access
  if (typeof access === 'undefined') {
    return undefined;
  }


  if (!access) {
    return (
      <ErrorPage
        message={t<string>("common.error.unauthorized", "Unauthorized")}
      />
    )
  }

  if (error) {
    return (
      <ErrorPage
        message={extractErrorMessage(error, t<string>("common.error.unknown", "Unknown error"))}
      />
    );
  }

  return (
    <ThemedPage children={children}/>
  );
}
